import * as React from "react";
import { useHistory, useLocation } from "react-router";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import qs from "qs";

import { URLS } from "@/utils";
import { Button, Card, Field, Input } from "@/components/ui";
import { Png } from "@/assets";
import { ArrowLeft } from "lucide-react";

const errorMessages: Record<string, string> = {
  user_not_found: "We couldn't find an account for your email address. Please contact your admin.",
  invalid_credentials: "The credentials provided were invalid. Please try again.",
  unsupported_domain: "SSO is not supported for your email domain. Please contact support for assistance.",
  access_denied: "Access was denied by the login provider.",
  connection_error: "There was a problem connecting to your SSO provider.",
  timeout: "The login attempt timed out. Please try again.",
  default: "An unexpected error occurred during sign in. Please try again.",
};

const ssoSchema = z.object({
  email: z.email().trim().min(1, "Email is Required"),
});

type SSOValues = z.infer<typeof ssoSchema>;

const SSOLoginPageBase: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const queryParams = qs.parse(location.search.slice(1));
    const message = queryParams["message"];
    if (message) {
      const errorKey = String(message);
      setServerError(errorMessages[errorKey] || errorMessages.default);
    }
  }, [location.search]);

  const form = useForm<SSOValues>({
    resolver: zodResolver(ssoSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: SSOValues) => {
    setIsSubmitting(true);
    setServerError(null);
    window.location.href = `/api/bedrock/integrations/sso/initiate?email=${encodeURIComponent(data.email)}`;
  };

  const toLogin = (): void => {
    history.push({
      pathname: URLS.login,
      state: location.state,
    });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-200px)] p-4 sm:p-6">
      <Card className="w-full max-w-[550px] p-6 sm:p-10 shadow-lg border-slate-200">
        <div className="flex flex-col mb-6 gap-2">
          <div className="flex justify-center">
            <img src={Png.Logo} alt="SocialCoach" className="w-48 sm:w-60" />
          </div>
          <p className="text-slate-600 text-center font-semibold text-lg">Single Sign-On (SSO)</p>
        </div>

        {serverError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md">{serverError}</div>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Work Email Address" error={fieldState.error?.message}>
                <Input {...field} type="email" placeholder="email@your-company.com" disabled={isSubmitting} />
              </Field>
            )}
          />

          <Button type="submit" size="lg" className="w-full h-11 sm:h-12 text-base mt-2" loading={isSubmitting}>
            Continue
          </Button>

          <div className="flex justify-center items-center py-4">
            <button
              type="button"
              onClick={toLogin}
              className="text-sm text-primary hover:text-slate-600 tracking-wide cursor-pointer flex flex-row items-center gap-2 font-sans"
            >
              <ArrowLeft size={15} />
              Return to standard login
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export const SSOLoginPage = SSOLoginPageBase;
